import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EVENT_TYPES,
  EXPERIENCE_LEVELS,
  TICKET_TYPES,
  TERRAIN_TYPES,
  EVENT_MODALITIES,
  isValidEventType,
  isValidExperienceLevel,
  isValidTerrainType
} from '../constants/eventTypes';

/**
 * Hook personalizado para gestionar categorías de eventos con traducciones
 * Centraliza el acceso a todas las tipologías y sus traducciones
 */
export const useEventCategories = () => {
  const { t } = useTranslation('eventCategories');

  // Tipos de eventos con traducciones
  const eventTypes = useMemo(() =>
    EVENT_TYPES.map(type => ({
      key: type,
      label: t(`type.${type}`, type),
      description: t(`type.${type}Description`, '')
    })), [t]
  );

  // Niveles de experiencia con traducciones
  const experienceLevels = useMemo(() =>
    EXPERIENCE_LEVELS.map(level => ({
      key: level,
      label: t(`experience.${level}`, level),
      description: t(`experience.${level}Description`, '')
    })), [t]
  );

  // Tipos de tickets con traducciones
  const ticketTypes = useMemo(() =>
    TICKET_TYPES.map(type => ({
      key: type,
      label: t(`ticket.${type}`, type),
      description: t(`ticket.${type}Description`, '')
    })), [t]
  );

  // Tipos de terreno con traducciones
  const terrainTypes = useMemo(() =>
    TERRAIN_TYPES.map(type => ({
      key: type,
      label: t(`terrain.${type}`, type),
      description: t(`terrain.${type}Description`, '')
    })), [t]
  );

  // Modalidades con traducciones
  const modalities = useMemo(() => [
    {
      key: 'withVehicle',
      value: true,
      label: t('modality.withVehicle', 'Con vehículo'),
      description: t('modality.withVehicleDescription', '')
    },
    {
      key: 'personal',
      value: false,
      label: t('modality.personal', 'Personal'),
      description: t('modality.personalDescription', '')
    }
  ], [t]);

  // Funciones helper
  const getEventTypeLabel = (type) => {
    const eventType = eventTypes.find(et => et.key === type);
    return eventType ? eventType.label : type;
  };

  const getExperienceLevelLabel = (level) => {
    const experienceLevel = experienceLevels.find(el => el.key === level);
    return experienceLevel ? experienceLevel.label : level;
  };

  const getTerrainTypeLabel = (terrain) => {
    const terrainType = terrainTypes.find(tt => tt.key === terrain);
    return terrainType ? terrainType.label : terrain;
  };

  const getTicketTypeLabel = (ticket) => {
    const ticketType = ticketTypes.find(tt => tt.key === ticket);
    return ticketType ? ticketType.label : ticket;
  };

  const getModalityLabel = (withVehicle) => {
    const modality = modalities.find(m => m.value === withVehicle);
    return modality ? modality.label : (withVehicle ? 'Con vehículo' : 'Personal');
  };

  return {
    // Datos con traducciones
    eventTypes,
    experienceLevels,
    ticketTypes,
    terrainTypes,
    modalities,

    // Funciones helper para obtener labels
    getEventTypeLabel,
    getExperienceLevelLabel,
    getTerrainTypeLabel,
    getTicketTypeLabel,
    getModalityLabel,

    // Funciones de validación
    isValidEventType,
    isValidExperienceLevel,
    isValidTerrainType,

    // Constantes originales (por compatibilidad)
    EVENT_TYPES,
    EXPERIENCE_LEVELS,
    TICKET_TYPES,
    TERRAIN_TYPES,
    EVENT_MODALITIES
  };
};